import { useEffect } from 'react';
import { isMac } from '../keymap/platform';
import { useDocStore } from '../store/docStore';
import { useToolsStore } from '../tools/toolsStore';
import { useSidebarStore } from '../sidebar/sidebarStore';
import { useFocusRegistry, type FocusZoneId } from './focusRegistry';

const ARROW_TO_ZONE: Record<string, FocusZoneId> = {
  ArrowLeft: 'sidebar',
  ArrowRight: 'outline',
  ArrowUp: 'editor',
  ArrowDown: 'tools',
};

function isDirectModifierCombo(e: KeyboardEvent): boolean {
  // Mac: Cmd + Option (and not Ctrl/Shift). Win/Linux: Ctrl + Alt (and not Meta/Shift).
  if (isMac()) {
    return e.metaKey && e.altKey && !e.ctrlKey && !e.shiftKey;
  }
  return e.ctrlKey && e.altKey && !e.metaKey && !e.shiftKey;
}

/**
 * Make the target zone visible before focusing it.
 * - tools  → open the bottom panel.
 * - sidebar → uncollapse if collapsed.
 * - outline → auto-pin if not pinned.
 * - editor → always visible; no-op.
 */
function autoShow(zoneId: FocusZoneId): void {
  if (zoneId === 'tools') {
    useToolsStore.getState().showPanel();
  } else if (zoneId === 'sidebar') {
    useSidebarStore.getState().toggleOpen(true);
  } else if (zoneId === 'outline') {
    if (!useDocStore.getState().outlinePinned) {
      useDocStore.setState({ outlinePinned: true });
    }
  }
}

/**
 * Mount once at the app root. Wires the global focus-zone shortcuts:
 *  - ⌥⌘ + Arrow / Ctrl+Alt + Arrow → direct jump (auto-shows the target zone)
 *  - F6 / Shift+F6 → cycle through visible zones
 */
export function useFocusZoneShortcuts(): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'F6') {
        e.preventDefault();
        // For cycle, autoShow happens after we know which zone is next.
        const dir = e.shiftKey ? -1 : 1;
        const visible = Array.from(useFocusRegistry.getState().zones.values())
          .filter((z) => z.isVisible())
          .sort((a, b) => a.order - b.order);
        if (visible.length === 0) return;
        const currentIdx = visible.findIndex((z) => z.id === useFocusRegistry.getState().currentZoneId);
        const next = visible[(currentIdx + dir + visible.length * 2) % visible.length];
        autoShow(next.id);
        useFocusRegistry.getState().focusById(next.id);
        return;
      }
      if (isDirectModifierCombo(e)) {
        const zoneId = ARROW_TO_ZONE[e.key];
        if (zoneId) {
          e.preventDefault();
          autoShow(zoneId);
          useFocusRegistry.getState().focusById(zoneId);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
